import './styles.css';
// dotlottie-wc registers <dotlottie-wc> custom element with thorvg renderer.
// Used by win-gift-modal as a more accurate TGS renderer for animations the
// bundled tgs-player (lottie-web) mishandles (e.g. tt:2 inverted track mattes).
import '@lottiefiles/dotlottie-wc';

/**
 * GiftGo — SPA Entry Point
 * 
 * Bootstrap sequence:
 * 1. Telegram WebApp ready + expand
 * 2. Theme detection (inline script already ran)
 * 3. Auth handshake FIRST (sets session cookie)
 * 4. Load user data via /api/spa_init (cookie is now set)
 * 5. i18n init
 * 6. Mount App Shell (nav + page container)
 * 7. Router init → navigate to initial page
 * 8. Hide preloader
 */

import store from './store.js';
import router from './router.js';
import BottomNav from './components/navigation/BottomNav.js';
import Preloader from './components/common/Preloader.js';
import { spaInit, loadUserData } from './api/user.js';
import { dispatchTasksCount } from './utils/tasksCount.js';
import { fetchTasksList } from './api/tasks.js';
import * as i18nModule from '../static/js/modules/i18n.js';
import { installHiddenLottieGuard } from './utils/lottie-idle.js';
import { preloadGenieStartup } from '../static/js/modules/genie-art.js';
import { loadEventPromo } from '../static/js/modules/event-promo-entry.js';

// Expose i18n globally for SPA page components
window.i18n = i18nModule;

// A Lottie sticker inside a hidden (display:none) page must not keep the whole
// app's animation loop alive — see src/utils/lottie-idle.js. Installed before
// any page mounts, so the prefetched pages are covered too.
installHiddenLottieGuard();

// ── App Shell ───────────────────────────────────────────────────────

const preloader = new Preloader();

/**
 * Ask Telegram for write_access (DM permission) if not already granted.
 * Idempotent: native popup remembers a prior "deny" and won't re-show
 * within a Telegram-defined window. Persists the outcome via
 * /api/auth/write_access — backend flips users.msg to 'enable' on grant
 * and 'not_found' on deny so mailing/notification filters can act on it.
 * Stores a per-trigger flag in localStorage to avoid re-asking the same
 * user on every reload within the same browser.
 * See docs/debt/2026-05-31-telegram-request-write-access.md.
 */
function _maybeRequestWriteAccess(tg, trigger) {
  try {
    if (!tg || typeof tg.requestWriteAccess !== 'function') return;
    const user = tg.initDataUnsafe?.user;
    if (user?.allows_write_to_pm) return;
    const dismissedKey = `gg_wa_dismissed_${trigger}`;
    try {
      if (localStorage.getItem(dismissedKey) === '1') return;
    } catch (_) {}
    tg.requestWriteAccess((granted) => {
      try { localStorage.setItem(dismissedKey, '1'); } catch (_) {}
      fetch('/api/auth/write_access', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ granted: !!granted, trigger }),
        credentials: 'include',
      }).catch(() => {});
    });
  } catch (_) {
    // Never let a popup error block bootstrap or any other caller.
  }
}
window._maybeRequestWriteAccess = _maybeRequestWriteAccess;

/**
 * Main SPA bootstrap sequence.
 * Initializes Telegram WebApp, authenticates, loads user data,
 * sets up i18n, mounts app shell with router, and hides preloader.
 */
/**
 * Per-user beacon id, set server-side as the `gg_bid` cookie and mirrored
 * into `<meta name="gg-bid">`. It is the single join key for the reach
 * funnel — every stage carries it in the request URL so the health-monitor
 * can reconstruct the per-user funnel from nginx access.log WITHOUT counting
 * by IP (NAT/VPN/roaming made IP-based counting unreliable; see
 * docs/debt/RKN_INCIDENT.md §9).
 */
function _beaconId() {
  return document.querySelector('meta[name="gg-bid"]')?.content || '';
}

/**
 * Fire the JS-stage beacon as early as possible — before Telegram WebApp
 * checks, before any other work. Proves the bundle was downloaded AND
 * executed (the body-img beacon only proves the HTML was parsed). The bid is
 * put in the URL (not just the body) so nginx logs it and the monitor can
 * join this stage to the HTML-stage beacons by bid.
 */
function _fireJsBeacon() {
  try {
    if (!('sendBeacon' in navigator)) return;
    const bid = _beaconId();
    const url = `/api/beacon/js?bid=${encodeURIComponent(bid)}`;
    const payload = JSON.stringify({ bid, ts: Date.now(), ua: navigator.userAgent.slice(0, 120) });
    navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
  } catch (_) {
    // sendBeacon is best-effort; never block bootstrap on a measurement signal
  }
}

/**
 * Fire the READY-stage beacon — the funnel's success anchor. Reached only
 * after the whole bootstrap succeeded (auth, user data via spa_init OR the
 * user_data fallback, i18n, app shell, first page) and the app is actually
 * visible to the user. A user who got here did NOT white-screen and did open
 * the app. bid in the URL so the monitor joins it by user.
 */
function _fireReadyBeacon() {
  try {
    if (!('sendBeacon' in navigator)) return;
    const bid = _beaconId();
    navigator.sendBeacon(`/api/beacon/ready?bid=${encodeURIComponent(bid)}`);
  } catch (_) {
    // best-effort; a measurement signal must never affect the running app
  }
}

async function bootstrap() {
  _fireJsBeacon();
  console.log('[GiftGo SPA] Bootstrap started');

  // 1. Telegram WebApp
  const tg = window.Telegram?.WebApp;
  if (!tg) {
    console.error('[GiftGo SPA] Telegram WebApp not available');
    window.location.href = '/auth_error';
    return;
  }

  tg.ready();
  tg.expand();
  if (tg.requestFullscreen) {
    try { tg.requestFullscreen(); } catch (_) {}
  }

  // 🔴 A vertical drag inside the app must never close it — the app closes by
  // its own button.
  //
  // The iOS client reads a downward drag that the web content does not cancel
  // as «pull down to close». Anywhere the page is not scrolling — and the
  // scratch ticket is exactly such a place, because its foil holds
  // `touch-action: none` so the finger can rub — the gesture falls through to
  // the client and the mini-app starts collapsing under the hand (Denis,
  // 13.09: «тру пальцем по области где стирать — приложение пытается
  // свернуться; посмотри как сделано в Giftlix»).
  //
  // `disableVerticalSwipes` is Telegram's own switch for it (Bot API 7.7+): it
  // turns off the close gesture without touching the DOM, so every scrolling
  // page keeps scrolling exactly as before. This is the same line Giftlix
  // carries in its own bootstrap. Older clients do not expose the method — the
  // ticket cancels the gesture itself as a second line of defence.
  if (typeof tg.disableVerticalSwipes === 'function') {
    try { tg.disableVerticalSwipes(); } catch (_) { /* older client */ }
  }

  // Show preloader (progress starts at 5%)
  const appRoot = document.getElementById('app');
  if (!appRoot) {
    console.error('[GiftGo SPA] #app container not found');
    return;
  }
  preloader.show(appRoot);

  // ── Step 1: Telegram user (10%) ─────────────────────────────────
  const telegramUser = tg.initDataUnsafe?.user;
  if (!telegramUser?.id) {
    console.error('[GiftGo SPA] No Telegram user in initData');
    window.location.href = '/auth_error';
    return;
  }

  store.setState({ telegramUser });
  preloader.setProgress(10);

  // ── Step 2: Auth handshake (20%) ────────────────────────────────
  const initData = tg.initData;
  const tgLang = telegramUser.language_code || 'en';
  // i18n starts loading in parallel (doesn't need auth)
  const i18nPromise = window.i18n?.initI18n ? window.i18n.initI18n(tgLang) : Promise.resolve();

  let userData = null;

  if (initData) {
    try {
      const hsResp = await fetch('/api/auth/handshake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ init_data: initData }),
        credentials: 'include',
      });
      const hsData = await hsResp.json();
      if (!hsData.success) {
        console.warn('[GiftGo SPA] Handshake failed:', hsData.error || 'unknown');
      }
    } catch (err) {
      console.warn('[GiftGo SPA] Handshake error:', err);
    }
  }
  // Trigger #1 (per docs/debt/2026-05-31-telegram-request-write-access.md):
  // ask for DM permission on first open if Telegram hasn't already granted it.
  _maybeRequestWriteAccess(tg, 'first_open');
  preloader.setProgress(20);

  // ── Step 3: Load user data (50%) ────────────────────────────────
  try {
    try {
      userData = await spaInit(telegramUser);
    } catch (_) {
      console.warn('[GiftGo SPA] /api/spa_init failed, falling back to /api/user_data');
      userData = await loadUserData(telegramUser);
    }
  } catch (err) {
    console.error('[GiftGo SPA] Failed to load user data:', err);
    appRoot.innerHTML = `
      <div class="spa-error">
        <p>Failed to load app data.</p>
        <button onclick="location.reload()">Retry</button>
      </div>
    `;
    return;
  }

  if (!userData?.success) {
    console.error('[GiftGo SPA] User data not successful:', userData);
    appRoot.innerHTML = `
      <div class="spa-error">
        <p>Server error. Please try again.</p>
        <button onclick="location.reload()">Retry</button>
      </div>
    `;
    return;
  }
  preloader.setProgress(50);

  // ── Step 4.5: Restore active Risk session (if any) ──────────────
  // Gated by the risk-system runtime flag — when it's off the backend
  // 404s /api/risk/session anyway, so skip the call entirely.
  // (core/feature_flags.py, docs/audit/2026-06-10-hide-risk-system-prod.md).
  if (window.RISK_ENABLED) {
    try {
      const { restoreRiskSession } = await import('./api/risk.js');
      const riskSession = await restoreRiskSession();
      if (riskSession?.success && riskSession.session) {
        setTimeout(async () => {
          if (window.i18n?.ready instanceof Promise) {
            await window.i18n.ready;
          }
          const { showRiskStairsModal } = await import('../static/js/modules/risk-stairs-modal.js');
          showRiskStairsModal(riskSession.session, { source: 'restore' });
        }, 600);
      }
    } catch (err) {
      console.warn('[GiftGo SPA] Risk session restore failed:', err);
    }
  }

  // ── Step 4: Populate store + appSettings (55%) ──────────────────
  const user = userData.user || userData;
  store.setState({
    user,
    gifts: userData.all_gifts || userData.gifts || {},
    userGifts: user.gifts || [],
    appSettings: userData.app_settings || window.appSettings || null,
    language: userData.language_code || user.language_code || localStorage.getItem('giftgo_user_language') || 'en',
    isAuthenticated: true,
    isLoading: false,
  });

  // Родная кнопка настроек Telegram (в меню «⋮» мини-приложения) открывает ту
  // же шторку языка/темы, что и шестерёнка в профиле. Раньше настройки висели
  // ещё и отдельной капсулой в шапке главной — владелец попросил убрать её из
  // строки (06.09.2026), чтобы там остались ровно три блока: лента, билеты,
  // кристаллы. Родная кнопка и шестерёнка в профиле её заменяют.
  try {
    if (tg.SettingsButton?.show) {
      const { openUserSettings } = await import('../static/js/modules/open-settings.js');
      tg.SettingsButton.onClick(() => openUserSettings(user.id));
      tg.SettingsButton.show();
    }
  } catch (err) {
    console.warn('[GiftGo SPA] SettingsButton wiring failed:', err);
  }

  // Populate window.appSettings from game_settings immediately.
  // game-logic.js reads LOTTERY_WHEELS / BET_GIFTS from window.appSettings
  // at render time. Without this, the first PlayPage render falls back to
  // showing ALL gifts, causing excessive image loads.
  if (userData.game_settings) {
    if (!window.appSettings) window.appSettings = {};
    if (userData.game_settings.bet_types) window.appSettings.BET_TYPES = userData.game_settings.bet_types;
    if (userData.game_settings.lottery_wheels) window.appSettings.LOTTERY_WHEELS = userData.game_settings.lottery_wheels;
    if (userData.game_settings.bet_gifts) window.appSettings.BET_GIFTS = userData.game_settings.bet_gifts;

    // Daily free case: the 'free' showcase carries synthetic tiles
    // (CRYSTALS_n / TICKETS_n) that don't exist in the gift library — merge
    // them into the gifts dictionary so every resolver (wheel renderer,
    // possible-wins list, win modal) treats them as first-class gifts.
    const freePool = userData.game_settings.bet_gifts?.free;
    if (freePool) {
      Object.assign(store.state.gifts, freePool);
    }
  }

  // Daily free case availability (tile ↔ timer state on the Play page)
  store.setState({
    dailyCase: userData.daily_case || { available: false, next_available_at: null },
  });

  // ── Event mode ────────────────────────────────────────────────
  if (userData.event_mode?.active) {
    // Inject event lottery wheels and bet gifts into appSettings
    // so game-logic.js getAvailableGiftsForBet() works transparently
    const evtSettings = userData.event_mode.game_settings;
    if (evtSettings) {
      Object.assign(window.appSettings.LOTTERY_WHEELS, evtSettings.lottery_wheels);
      Object.assign(window.appSettings.BET_GIFTS, evtSettings.bet_gifts);

      // Same reason as the free pool above: an event's showcase may hold cards
      // that exist only in its pool (the bonus format's crystals card), and
      // every resolver reads the gift dictionary, not the pool. Only ids the
      // library lacks are merged, so an event can never overwrite real catalog
      // data with a display copy.
      for (const pool of Object.values(evtSettings.bet_gifts || {})) {
        for (const [id, card] of Object.entries(pool || {})) {
          if (!(id in store.state.gifts)) store.state.gifts[id] = card;
        }
      }
    }
  }

  // Save event mode data in store for PlayPage
  store.setState({
    eventMode: userData.event_mode || { active: false },
  });
  const genieReady = preloadGenieStartup(store.state.eventMode, store.state.gifts);
  // 🍬 The Sweet night and its candies warm up the same way; its module loads only for Sweet.
  if (store.state.eventMode?.active && store.state.eventMode.config?.mode === 'sweet') {
    void import('../static/js/modules/sweet-c1.js')
      .then((sweet) => sweet.preloadSweetStartup(store.state.eventMode)).catch(() => {});
  }
  // 🎃 Halloween as the Genie (owner 28.10, TG 15773: nothing seen loading on the event page): its first screen - the
  // tile, the backdrop, the promo, both wheels and their showcase - is decoded behind this preloader (at most 9 s), then
  // the street, the witches, the hosts and the sounds are warmed after it. From the start data alone (never a read of
  // its API); its module loads only for Halloween.
  const halloweenReady = store.state.eventMode?.active && store.state.eventMode.config?.mode === 'halloween'
    ? import('../static/js/modules/halloween-preload.js')
      .then((warm) => warm.preloadHalloweenStartup(store.state.eventMode, store.state.gifts)).catch(() => {})
    : Promise.resolve();

  // The promo sheet lives in the lazy "modals" chunk (~350 kB). Waiting until
  // reveal to start fetching it is most of why the sheet used to arrive a
  // moment after the app instead of with it (Denis 2026-09-05). Start the fetch
  // here, the instant we know an event is running, so by reveal it is parsed and
  // the sheet opens in the same frame. Best-effort: the call site awaits this
  // same promise and still handles a failure.
  const eventPromoModule = loadEventPromo(store.state.eventMode)?.catch(() => null);

  // Start preloading thumbnails for default bet ('low') in parallel
  const thumbnailsReady = _warmFirstBetThumbnails(store.state.gifts);
  // Preload win modal icons (SVGs re-created on every modal open)
  _warmWinModalIcons();
  preloader.setProgress(55);

  // ── Step 5: i18n (65%) ──────────────────────────────────────────
  await i18nPromise;
  const actualLang = store.state.language;
  if (actualLang !== tgLang && window.i18n?.initI18n) {
    await window.i18n.initI18n(actualLang);
  }
  preloader.setProgress(65);

  // ── Step 6: Build App Shell (70%) ───────────────────────────────
  const savedTheme = localStorage.getItem('gg_theme') || 'auto';
  store.setState({ theme: savedTheme });

  const pageContainer = document.createElement('div');
  pageContainer.id = 'spa-page-container';
  pageContainer.className = 'spa-page-container';
  pageContainer.style.visibility = 'hidden';
  appRoot.appendChild(pageContainer);

  // 🔴 A tap on the tab you are ALREADY on is «take me back to the top of this
  // tab». The router returns silently when the path has not changed
  // (router.js), and /play has a screen of its own inside the page — the
  // scratch ticket — so the page is told instead of the router.
  const bottomNav = new BottomNav((path) => {
    if (router.currentPath === path) {
      document.dispatchEvent(new CustomEvent('nav:reselect', { detail: { path } }));
      return;
    }
    router.navigateTo(path);
  });
  bottomNav.render(appRoot);
  bottomNav.element.style.visibility = 'hidden';

  // Аватарка пользователя вместо иконки профиля в навбаре
  const navUser = () => {
    const u = store.state.user || userData.user || userData || {};
    const tg = store.state.telegramUser || {};
    return { ...u, photo_url: u.photo_url || tg.photo_url, first_name: u.first_name || tg.first_name };
  };
  bottomNav.updateAvatar(navUser());
  store.subscribe('user', () => bottomNav.updateAvatar(navUser()));

  // Show ticket badge from initial user data
  const initTicketBalance = userData.ticket_balance ?? (userData.user || userData).ticket_balance;
  const raffleJoined = userData.raffle_joined ?? false;
  if (initTicketBalance > 0) {
    bottomNav.updateBadge('/events', initTicketBalance);
  } else if (!raffleJoined) {
    // Balance is 0 and not joined → show "1" to hint free participation
    bottomNav.updateBadge('/events', 1);
  }

  if (window.i18n?.t) {
    bottomNav.updateTranslations(window.i18n.t);
  }

  store.subscribe('language', () => {
    if (window.i18n?.t) {
      bottomNav.updateTranslations(window.i18n.t);
    }
  });
  preloader.setProgress(70);

  // ── Step 7: Mount first page (85%) ──────────────────────────────
  await router.navigateToFirst(pageContainer, bottomNav);
  preloader.setProgress(85);

  // ── Step 8: Wait for visible images (95%) ───────────────────────
  await Promise.all([
    _preloadVisibleImages(pageContainer),
    thumbnailsReady,
    genieReady,
    halloweenReady,
  ]);
  preloader.setProgress(95);

  // ── Step 8b: Lock page scroll ────────────────────────────────────
  import('./utils/scroll-lock.js').then(m => m.initScrollLock());

  // ── Step 9: Reveal (100%) ───────────────────────────────────────
  pageContainer.style.visibility = 'visible';
  bottomNav.element.style.visibility = 'visible';
  preloader.hide();

  // Prewarm dotlottie-wc renderer (used by win + preview modals).
  // Without this the very first modal open after bootstrap can race
  // against thorvg wasm load and render a blank canvas.
  import('../static/js/modules/gift-actions.js').then(m => {
    if (typeof m.prewarmDotLottiePlayer === 'function') {
      m.prewarmDotLottiePlayer();
    }
  }).catch(() => {});

  // Signal that the app is visible (pages can start entrance animations)
  document.dispatchEvent(new CustomEvent('app:ready'));

  // 🍬 The Sweet reels' pictures are fetched and drawn ahead once the app is visible (owner 25.10: the reels appear
  // whole when the tile opens), from the start data alone - never a read of the Sweet API. Other events pay nothing.
  if (store.state.eventMode?.active && store.state.eventMode.config?.mode === 'sweet') {
    void import('../static/js/modules/sweet-reel-art.js')
      .then((reels) => reels.preloadSweetReels(store.state.eventMode, store.state.gifts)).catch(() => {});
  }

  // Prime the Tasks nav badge with the count of still-available tasks, so it
  // shows before the user ever opens the Tasks page (mirrors the raffle-ticket
  // badge). Best-effort and non-blocking; the Tasks page keeps it live after.
  (async () => {
    try {
      const uid = telegramUser?.id;
      if (!uid) return;
      // Coalesced: shares the same in-flight request as the page prefetch /
      // activation instead of firing a third competing POST (which tripped the
      // per-user cooldown → 429 → false "failed to load").
      const result = await fetchTasksList(uid);
      if (result?.success) dispatchTasksCount(result.tasks || []);
    } catch (_) { /* badge is best-effort */ }
  })();

  // Reach-funnel success anchor: the app is now visible to the user. Fired
  // here (after every bootstrap step incl. the user_data fallback) so the
  // health-monitor can tell "opened the app" from "white screen / failed to
  // load" by bid, not by IP. Best-effort; never affects the running app.
  _fireReadyBeacon();

  // ── Post-reveal modals (language confirm, bot status) ───────────
  // Must happen after app is visible so modals render on top of the app shell.
  _showPostRevealModals(userData);

  // Show event promo modal (every app open while event is active). The chunk
  // has been downloading since step 4, so this normally resolves immediately.
  if (eventPromoModule) {
    eventPromoModule.then(m => {
      if (!m) return;
      m.maybeShowEventPromo(
        store.state.eventMode,
        store.state.gifts,
        (path) => router.navigateTo(path),
      );
    }).catch(e => console.warn('[EventPromo] load error:', e));
  }

  // Free-case promo — shown LAST (after language / activate-chat / bot-status
  // sheets), once per app open, only when the daily case is ready. We wait for
  // each EXPECTED sheet to actually appear AND clear (not a timer), so the promo
  // can't slip into the gap between two chained sheets.
  //
  // While an event is active its own promo sheet already greets the user on every
  // open — stacking the free-case sheet behind it was confusing (Denis 2026-07-18).
  // So we suppress the free-case promo entirely whenever an event is active; the
  // daily case stays reachable from its section/button, just without a second sheet.
  if (!store.state.eventMode?.active) {
    const _hardExpected = [];
    if (userData.language_just_set) _hardExpected.push('#languageConfirmModalOverlay');
    if (userData.bot_status?.is_blocked) _hardExpected.push('#botBlockedModalOverlay');
    else if (userData.bot_status?.is_chat_unavailable) _hardExpected.push('#firstLoginBonusModalOverlay');
    _scheduleFreeCasePromo(
      (path) => router.navigateTo(path),
      _hardExpected,
      false,
    );
  }

  // Listen for language changes from confirm modal (or other sources)
  document.addEventListener('language:changed', async (e) => {
    const lang = e.detail?.language;
    if (lang) {
      store.setState({ language: lang });
      await router.notifyLanguageChange();
    }
  });

  // Prefetch remaining pages in background (eliminates navigation latency)
  router.prefetchAll();

  // Preload all thumbnails + animations into browser cache (P1→P2→P3 priority)
  import('./services/preloadManager.js').then(m => m.startPreloading());

  // Listen for theme changes from Telegram
  tg.onEvent?.('themeChanged', () => {
    const newTheme = tg.colorScheme;
    document.documentElement.setAttribute('data-theme', newTheme);
    store.setState({ theme: newTheme });
  });

  console.log('[GiftGo SPA] Bootstrap complete ✅');
}

/**
 * Wait for visible <img> elements to load (with timeout).
 * Only waits for images actually inside the viewport rectangle
 * (both vertical AND horizontal bounds). This avoids waiting for
 * off-screen barrel items in the horizontal scroll strip.
 */
async function _preloadVisibleImages(container, timeoutMs = 2000) {
  const imgs = container.querySelectorAll('img');
  const viewportW = window.innerWidth;
  const viewportH = window.innerHeight;
  const visible = [];

  imgs.forEach(img => {
    if (img.complete) return;  // Already loaded / cached
    const rect = img.getBoundingClientRect();
    // Must be inside the viewport on BOTH axes and have non-zero size
    if (rect.width > 0 && rect.height > 0 &&
        rect.top < viewportH && rect.bottom > 0 &&
        rect.left < viewportW && rect.right > 0) {
      visible.push(new Promise(resolve => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      }));
    }
  });

  if (visible.length === 0) return;

  // Race: all visible images loaded OR timeout
  const timeout = new Promise(resolve => setTimeout(resolve, timeoutMs));
  await Promise.race([Promise.all(visible), timeout]);
}

/**
 * Preload SVG icons used by the win modal into the browser decoded-image cache.
 * The modal destroys and recreates DOM on every open (innerHTML = ''),
 * so without pre-caching, icons flash in ~0.5s after the modal appears.
 */
function _warmWinModalIcons() {
  const urls = [
    '/static/images/icons/gift_icons.svg',
    '/static/images/icons/save_icons.svg',
    '/static/images/icons/share_icons.svg',
    '/static/images/icons/sell_icons.svg',
    '/static/images/icons/crystal.svg',
  ];
  urls.forEach(url => {
    const img = new Image();
    img.src = url;
  });
}

/**
 * Preload thumbnail images for the default bet tier into the browser cache.
 * Uses Image objects so the cache is shared with <img> elements in the DOM.
 * Resolves when all thumbnails are loaded (or on error), with a safety timeout.
 *
 * @param {Object} gifts — gift library from store.state.gifts
 * @returns {Promise<void>}
 */
function _warmFirstBetThumbnails(gifts) {
  const wheels = window.appSettings?.LOTTERY_WHEELS;
  const giftIds = wheels?.low || [];
  if (!giftIds.length || !gifts) return Promise.resolve();

  const seen = new Set();
  const promises = [];

  giftIds.forEach(id => {
    const gift = gifts[id];
    if (!gift) return;
    const url = gift.thumbnail_path;
    if (!url || seen.has(url)) return;
    seen.add(url);

    promises.push(new Promise(resolve => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = resolve;
      img.src = url;
    }));
  });

  if (!promises.length) return Promise.resolve();

  // Safety timeout: don't block bootstrap forever
  const timeout = new Promise(resolve => setTimeout(resolve, 3000));
  return Promise.race([Promise.all(promises), timeout]);
}

/**
 * Show post-reveal modals in priority order:
 * 1. Language confirm (new user) → when dismissed, fires 'language:confirmed' event
 * 2. Bot blocked / chat unavailable
 */
function _showPostRevealModals(userData) {
  const botBlocked = userData.bot_status?.is_blocked;
  const chatUnavailable = userData.bot_status?.is_chat_unavailable;
  const needsBotModal = botBlocked || chatUnavailable;

  // 1. Language confirmation for new users
  if (userData.language_just_set) {
    import('../static/js/modules/language-confirm-modal.js').then(m => {
      m.showLanguageConfirmModal(userData.language_code || 'en');
    }).catch(e => console.warn('[Modal] language-confirm load error:', e));

    // After language modal closes → show bot modal if needed
    if (needsBotModal) {
      document.addEventListener('language:confirmed', () => {
        setTimeout(() => _showBotModal(botBlocked, userData.bot_status?.bot_username), 400);
      }, { once: true });
    }
    return;
  }

  // 2. Bot blocked → warning; chat unavailable → "get 1 crystal" incentive
  if (needsBotModal) {
    _showBotModal(botBlocked, userData.bot_status?.bot_username);
  }
}

/**
 * True while any onboarding sheet is on screen. These sheets stay in the DOM
 * (hidden) and toggle a `show` class, so we test visibility by that class, not
 * by presence. Covers language-confirm, activate-chat, bot-blocked and the
 * event-promo sheet — everything the free-case promo must queue behind.
 */
function _anyOnboardingSheetVisible() {
  return [
    '#languageConfirmModalOverlay.show',
    '#firstLoginBonusModalOverlay.show',
    '#botBlockedModalOverlay.show',
    '.event-promo-overlay.show',
  ].some(sel => document.querySelector(sel));
}

/**
 * Show the free-case promo as the LAST sheet the user sees, once per app open,
 * only while the case is ready.
 *
 * Correctness comes from waiting for each EXPECTED sheet to *appear and then
 * clear* — not from a wall-clock "nothing visible" streak, which fires in the
 * gap between two chained sheets (language → activate-chat) or when a sheet's
 * chunk loads slowly. `eventExpected` is soft: the event promo may legitimately
 * not appear (e.g. no event gifts), so we wait a bounded grace for it.
 *
 * @param {(path: string) => void} navigate — router.navigateTo
 * @param {string[]} hardExpected — overlay selectors that WILL appear (language,
 *   bot/activate-chat); each must be seen and cleared before the promo shows.
 * @param {boolean} eventExpected — the event promo may appear (~≤1.2s in).
 */
function _scheduleFreeCasePromo(navigate, hardExpected, eventExpected) {
  try {
    if (!store.state.dailyCase?.available) return;
    if (sessionStorage.getItem('fcp_shown') === '1') return;
  } catch (_) { /* sessionStorage may be unavailable */ }

  const show = () => {
    import('../static/js/modules/free-case-promo-modal.js').then(m => {
      // The module sets the once-per-session flag only after it actually
      // appends the sheet (so a failed load retries next open).
      const shown = m.maybeShowFreeCasePromo(store.state.dailyCase, store.state.gifts, navigate);
      if (shown) { try { sessionStorage.setItem('fcp_shown', '1'); } catch (_) { /* ignore */ } }
    }).catch(e => console.warn('[FreeCasePromo] load error:', e));
  };

  const EVENT_SEL = '.event-promo-overlay';
  const EVENT_GRACE = 2500;   // event promo has appeared by now if it will
  const SETTLE_AFTER_CLEAR = 400;
  const MAX_WAIT = 60000;     // ultimate fallback if a sheet never resolves

  // Nothing expected at all → existing user, no event → show immediately.
  if (!hardExpected.length && !eventExpected && !_anyOnboardingSheetVisible()) {
    show();
    return;
  }

  const start = Date.now();
  const seen = new Set();
  let allClearSince = 0;

  const visible = (sel) => !!document.querySelector(sel + '.show');

  const tick = () => {
    const elapsed = Date.now() - start;
    // Ultimate fallback: after the cap, only show if nothing is on screen —
    // never stack on top of a sheet the user is still looking at.
    if (elapsed > MAX_WAIT) { if (!_anyOnboardingSheetVisible()) show(); return; }

    // Record any expected/event sheet that has become visible at least once.
    for (const sel of hardExpected) if (visible(sel)) seen.add(sel);
    if (eventExpected && visible(EVENT_SEL)) seen.add(EVENT_SEL);

    // Every hard sheet must have appeared and now be gone.
    const hardDone = hardExpected.every(sel => seen.has(sel) && !visible(sel));
    // Event: done when it appeared and cleared, or the grace passed unshow­n.
    const eventDone = !eventExpected
      || (seen.has(EVENT_SEL) ? !visible(EVENT_SEL) : elapsed > EVENT_GRACE);

    if (hardDone && eventDone && !_anyOnboardingSheetVisible()) {
      if (!allClearSince) allClearSince = Date.now();
      if (Date.now() - allClearSince >= SETTLE_AFTER_CLEAR) { show(); return; }
    } else {
      allClearSince = 0;
    }
    setTimeout(tick, 200);
  };
  tick();
}

/**
 * Show the appropriate bot modal:
 * - blocked → "unblock bot" warning (bot-blocked-modal)
 * - chat unavailable → "get 1 crystal" incentive (activate-chat-modal)
 */
function _showBotModal(isBlocked, botUsername) {
  if (isBlocked) {
    import('../static/js/modules/bot-blocked-modal.js').then(m => {
      m.showBotBlockedModal(botUsername || 'GiftGoBot');
    }).catch(e => console.warn('[Modal] bot-blocked load error:', e));
  } else {
    import('../static/js/modules/activate-chat-modal.js').then(m => {
      m.showActivateChatModal();
    }).catch(e => console.warn('[Modal] activate-chat load error:', e));
  }
}

// ── Launch ───────────────────────────────────────────────────────────

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
